import React from 'react';
import { Stethoscope, Check } from 'lucide-react';
import { IoMdClose } from 'react-icons/io';
import { IoChevronDown } from 'react-icons/io5';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { usePendingIntegrations } from '@/hooks/usePendingIntegrations';

export default function ToolsButton({ onOpenModal }) {
  const {
    hasPending,
    justCompleted,
    justErrored,
    integrations,
    toggleIntegration,
    healthStatus,
    checkAllHealth,
  } = usePendingIntegrations({ pollingInterval: 30000 });

  const hasIntegrations = integrations.length > 0;
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  // Check health once when dropdown opens
  React.useEffect(() => {
    if (!isDropdownOpen || integrations.length === 0) return;
    
    checkAllHealth();
  }, [isDropdownOpen]);

  // Status indicators
  const [showSuccessIndicator, setShowSuccessIndicator] = React.useState(false);
  const [showErrorIndicator, setShowErrorIndicator] = React.useState(false);

  // Show green checkmark for 2 seconds when integration completes
  React.useEffect(() => {
    if (justCompleted) {
      setShowSuccessIndicator(true);
      const timeout = setTimeout(() => setShowSuccessIndicator(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [justCompleted]);

  // Show red X for 2 seconds when integration errors
  React.useEffect(() => {
    if (justErrored) {
      setShowErrorIndicator(true);
      const timeout = setTimeout(() => setShowErrorIndicator(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [justErrored]);

  const StatusIndicator = () => {
    if (showSuccessIndicator) {
      return (
        <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-green-500 p-0.5">
          <Check className="h-full w-full text-white" strokeWidth={2.5} />
        </span>
      );
    }
    if (showErrorIndicator) {
      return (
        <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 p-0.5">
          <IoMdClose className="h-full w-full text-white" />
        </span>
      );
    }
    if (hasPending) {
      return (
        <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
        </span>
      );
    }
    return null;
  };

  if (hasIntegrations) {
    return (
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="rounded-full gap-2 h-[2.375rem] px-4 relative"
          >
            <Stethoscope className="h-4 w-4" />
            <span className="text-sm font-medium">Tools</span>
            <StatusIndicator />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <div className="max-h-[18rem] overflow-y-auto">
            {integrations.map((integration) => {
              const health = healthStatus[integration._id];
              const isHealthy = health?.healthy;
              
              // Determine status color
              let statusColor = 'bg-green-500'; // default success = healthy
              let statusTitle = 'Healthy';
              
              if (integration.status === 'pending') {
                statusColor = 'bg-amber-500';
                statusTitle = 'Processing...';
              } else if (integration.status === 'error') {
                statusColor = 'bg-red-500';
                statusTitle = integration.error?.message || 'Error';
              } else if (integration.status === 'success') {
                if (isHealthy === false) {
                  statusColor = 'bg-red-500';
                  statusTitle = health?.error || 'Unhealthy';
                } else if (isHealthy === undefined) {
                  statusColor = 'bg-green-500'; // default to green while checking
                  statusTitle = 'Checking...';
                }
              }
              
              return (
                <DropdownMenuItem
                  key={integration._id}
                  className="flex items-center justify-between gap-2 cursor-default"
                  onSelect={(e) => e.preventDefault()}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span 
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColor}`}
                      title={statusTitle}
                    />
                    <span className="text-sm font-medium truncate">{integration.name}</span>
                  </div>
                  {integration.status === 'success' && (
                    <Switch
                      checked={integration.enabled ?? true}
                      onCheckedChange={(enabled) =>
                        toggleIntegration({
                          id: integration._id,
                          enabled,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onOpenModal}>
            <span className="text-sm font-medium">Manage Tools</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="rounded-full gap-2 h-[2.375rem] px-4 relative"
      onClick={onOpenModal}
    >
      <Stethoscope className="h-4 w-4" />
      <span className="text-sm font-medium">Tools</span>
      <StatusIndicator />
    </Button>
  );
}
